import React, { useEffect } from "react";
import { observer, useLocalObservable } from "mobx-react-lite";

import appStore from "../../storage/AppStore";
import systemStore from "../../storage/SystemStore";

import Btn from "../../ui/Btn";
import Input from "../../ui/Input";
import Select from "../../ui/Select";

import TeamMembers from "../../models/TeamMembers";
import GameTeamMembers from "../../models/GameTeamMembers";

/**
 * AwardEditor
 *
 * Key behaviour:
 * - scope MATCH/TOURNAMENT uses game_team_id + company_user_id
 * - scope TRAINING uses team_id + company_user_id
 * - scope CLUB/SEASON:
 *    - can award company_user_id without selecting any team
 *    - optionally allow team_id (company team) if provided in props
 *
 * For individual MATCH (no game teams):
 * - team dropdown hidden
 * - player dropdown uses `individualPlayers` if provided or from game.player_1/player_2
 */
const AwardEditor = ({
  role = "MGR",
  scope = "MATCH",              // MATCH|TOURNAMENT|SEASON|CLUB|TRAINING
  awardStore,
  award = null,

  context = {},                 // {company_id,tournament_id,game_id,team_class_id}
  sport_id = null,

  // Team dropdown options (already normalized: [{id,name}])
  // For MATCH/TOURNAMENT: pass game teams
  // For TRAINING/CLUB/SEASON: pass company teams (optional)
  teams = [],

  // Optional, for individual games (no game_team_1/2): player options [{id,name}] where id=company_user_id
  individualPlayers = [],

  onSaved = null,
  onCancel = null,
  className = ""
}) => {
  const form = useLocalObservable(() => ({
    id: 0,
    award_type_id: null,

    recipient_kind: "USER", // USER|TEAM

    // recipients
    team_id: null,            // company_teams
    game_team_id: null,       // game_teams
    company_user_id: null,    // company_users

    name: "",
    was_validated: false,

    // member loaders
    teamMembers: new TeamMembers(null),
    gameTeamMembers: new GameTeamMembers(null),

    setProperty(k, v) { this[k] = v; },
    resetUser() { this.company_user_id = null; }
  }));

  const canEdit = role === "MGR" || role === "TRN";

  const useGameTeams = (scope === "MATCH" || scope === "TOURNAMENT");
  const useCompanyTeams = (scope === "TRAINING" || scope === "CLUB" || scope === "SEASON");

  const requiresTeam =
    (scope === "TRAINING") ||
    // For MATCH/TOURNAMENT: team required only when awarding TEAM,
    // because individual games may have no teams.
    false;

  const hasTeamsInUI = (teams || []).length > 0;
  const hasIndividualPlayers = (individualPlayers || []).length > 0;

  // Initialize store filters + form values
  useEffect(() => {
    if (!awardStore) return;

    awardStore.setContext({
      company_id: context.company_id ?? null,
      tournament_id: context.tournament_id ?? null,
      game_id: context.game_id ?? null,
      team_class_id: context.team_class_id ?? null
    });

    awardStore.setTypeFilter({
      sport_id: sport_id ?? null,
      scope,
      include_common: true
    });

    awardStore.getAwardTypes().catch(console.error);

    if (award && award.id) {
      form.id = award.id;
      form.award_type_id = award.award_type_id ?? null;
      form.name = award.name ?? "";

      form.team_id = award.team_id ?? null;
      form.game_team_id = award.game_team_id ?? null;
      form.company_user_id = award.company_user_id ?? null;

      // Determine kind
      form.recipient_kind = award.company_user_id ? "USER" : "TEAM";
    } else {
      form.id = 0;
      form.award_type_id = null;
      form.name = "";
      form.recipient_kind = "USER";
      form.team_id = null;
      form.game_team_id = null;
      form.company_user_id = null;
    }
  }, [award?.id, scope, sport_id, context.company_id, context.tournament_id, context.game_id, context.team_class_id]);

  // When selected team changes -> load members based on scope
  useEffect(() => {
    if (useGameTeams) {
      if (!form.game_team_id) {
        form.gameTeamMembers.clear();
        form.resetUser();
        return;
      }
      form.gameTeamMembers.setTeam(form.game_team_id);
      form.gameTeamMembers.load({company_id: context.company_id, game_id: context.game_id, team_id: form.game_team_id }).catch(console.error);
      return;
    }

    if (useCompanyTeams) {
      const companyId = context.company_id;
      if (!companyId || !form.team_id) {
        form.teamMembers.clear();
        form.resetUser();
        return;
      }
      form.teamMembers.setTeam(form.team_id);
      form.teamMembers.load({ company_id: companyId, team_id: form.team_id }).catch(console.error);
    }
  }, [form.game_team_id, form.team_id, context.company_id, useGameTeams, useCompanyTeams]);

  // TEAM kind disables user
  useEffect(() => {
    if (form.recipient_kind === "TEAM") form.resetUser();
  }, [form.recipient_kind]);

  const memberOptions = (() => {
    if (useGameTeams) {
      // If no teams exist (individual game), use the provided players list
      if (!hasTeamsInUI) return (individualPlayers || []);
      return form.gameTeamMembers.members;
    }
    // company teams
    if (!form.team_id) return [];
    return form.teamMembers.members;
  })();

  const validate = () => {
    form.was_validated = true;

    if (!form.award_type_id) return false;

    // Scope-context requirements:
    if (scope === "MATCH" && !context.game_id) return false;
    if (scope === "TOURNAMENT" && !context.tournament_id) return false;
    if (scope === "TRAINING" && !context.team_class_id) return false;
    if (scope === "CLUB" && !context.company_id) return false;
    if (scope === "SEASON" && !context.company_id && !context.tournament_id) return false;

    // TEAM requirement:
    // - TRAINING: team_id required (company team)
    if (scope === "TRAINING" && !form.team_id) return false;

    // Recipient requirement:
    if (form.recipient_kind === "TEAM") {
      // for team awards:
      if (useGameTeams) {
        if (hasTeamsInUI && !form.game_team_id) return false; // if there are teams, choose one
        // if no teams in UI (individual game), team award is impossible -> invalid
        if (!hasTeamsInUI) return false;
      } else {
        if (!form.team_id) return false;
      }
    } else {
      // USER award: require company_user_id
      if (!form.company_user_id) return false;

      // If game teams exist and you want team context for MATCH user awards, require game_team_id:
      // if (useGameTeams && hasTeamsInUI && !form.game_team_id) return false;
    }

    return true;
  };

  const onSave = async () => {
    if (!canEdit) return;
    if (!validate()) return;

    const payload = {
      award_type_id: form.award_type_id,
      name: form.name ? form.name : null,

      company_id: context.company_id ?? undefined,
      tournament_id: context.tournament_id ?? undefined,
      game_id: context.game_id ?? undefined,
      team_class_id: context.team_class_id ?? undefined,

      // recipients:
      team_id: useCompanyTeams ? (form.team_id ?? null) : null,
      game_team_id: useGameTeams ? (form.game_team_id ?? null) : null,
      company_user_id: form.recipient_kind === "USER" ? form.company_user_id : null
    };

    let ret;
    if (!form.id) ret = await awardStore.createAward(payload);
    else ret = await awardStore.updateAward({ award_id: form.id, ...payload });

    if (ret && ret.result === 0) onSaved?.(ret.award);
  };

  // Labels
  const teamLabel = useGameTeams ? (appStore.tr.team ?? "Team") : (appStore.tr.team ?? "Team");
  const playerLabel = appStore.tr.player ?? "Player";

  return (
    <div className={`card p-2 ${className}`}>
      <div className="flex align-items-center justify-content-between mb-2">
        <div className="font-bold">
          {form.id ? appStore.tr.edit_award : appStore.tr.new_award}
        </div>
        <div className="flex gap-2">
          {onCancel && <Btn icon="pi pi-times" outlined severity="secondary" onClick={onCancel} />}
          <Btn icon="pi pi-check" severity="success" onClick={onSave} disabled={!canEdit || systemStore.loading} />
        </div>
      </div>

      {/* Award type */}
      <div className="mb-2">
        <Select
          label={appStore.tr.award_type}
          store={form}
          prop="award_type_id"
          options={awardStore?.award_types || []}
          optionLabel="name"
          optionValue="id"
          disabled={!canEdit || systemStore.loading}
        />
        {form.was_validated && !form.award_type_id && (
          <small className="p-error">{appStore.tr.required}</small>
        )}
      </div>

      {/* Recipient kind */}
      <div className="mb-2">
        <Select
          label={appStore.tr.recipient}
          store={form}
          prop="recipient_kind"
          options={[
            { id: "USER", name: playerLabel },
            { id: "TEAM", name: teamLabel }
          ]}
          optionLabel="name"
          optionValue="id"
          disabled={!canEdit || systemStore.loading}
        />
      </div>

      {/* Team selector (if teams exist or if training requires it) */}
      {(useCompanyTeams || hasTeamsInUI) && (
        <div className="mb-2">
          {useGameTeams ? (
            <Select
              label={teamLabel}
              store={form}
              prop="game_team_id"
              options={teams || []}
              optionLabel="name"
              optionValue="id"
              disabled={!canEdit || systemStore.loading || !hasTeamsInUI}
              onChange={() => form.resetUser()}
            />
          ) : (
            <Select
              label={teamLabel}
              store={form}
              prop="team_id"
              options={teams || []}
              optionLabel="name"
              optionValue="id"
              disabled={!canEdit || systemStore.loading}
              onChange={() => form.resetUser()}
            />
          )}

          {/* Required hint */}
          {form.was_validated && (
            <>
              {scope === "TRAINING" && !form.team_id && (
                <small className="p-error">{appStore.tr.required}</small>
              )}
              {form.recipient_kind === "TEAM" && useGameTeams && hasTeamsInUI && !form.game_team_id && (
                <small className="p-error">{appStore.tr.required}</small>
              )}
            </>
          )}
        </div>
      )}

      {/* Player selector */}
      <div className="mb-2">
        <Select
          label={playerLabel}
          store={form}
          prop="company_user_id"
          options={memberOptions}
          optionLabel="name"
          optionValue="id"
          disabled={
            !canEdit ||
            systemStore.loading ||
            form.recipient_kind !== "USER" ||
            // If game teams exist, require selecting team first to filter members
            (useGameTeams && hasTeamsInUI && !form.game_team_id) ||
            // If training/team-based, require team_id first
            (scope === "TRAINING" && !form.team_id)
          }
        />
        {form.recipient_kind === "USER" && form.was_validated && !form.company_user_id && (
          <small className="p-error">{appStore.tr.required}</small>
        )}
      </div>

      {/* Custom title */}
      <div className="mb-2">
        <Input
          type="text"
          label={appStore.tr.custom_title ?? "Custom Title"}
          store={form}
          prop="name"
          disabled={!canEdit || systemStore.loading}
        />
      </div>
    </div>
  );
};

export default observer(AwardEditor);